// Herhalende reeksen volgens ADR 0015 (invi-wiki): maximaal 3 kalendermaanden
// vooruit, alleen wekelijks/tweewekelijks/maandelijks, te verlengen met
// maximaal 3 maanden na de laatste datum. Framework-vrij zodat het
// unit-testbaar blijft.

import { DateTime } from 'luxon'
import fetchJson from 'react-admin-loopback/lib/fetch'
import { config } from '../config'

export const ZONE = 'Europe/Amsterdam'
export const MAX_SERIES_MONTHS = 3
export const NOT_RECURRING = 'none'
export const WEEKLY = '7,day'
export const SUPPORTED_RECURRING = ['7,day', '14,day', '1,month']

export const RECURRING_CHOICES = [
  { id: NOT_RECURRING, name: 'Niet herhalen' },
  { id: '7,day', name: 'Wekelijks' },
  { id: '14,day', name: 'Tweewekelijks' },
  { id: '1,month', name: 'Maandelijks' },
]

// ISO-weekdagen: 1 = maandag … 7 = zondag
export const WEEKDAY_CHOICES = [
  { id: 1, name: 'ma' },
  { id: 2, name: 'di' },
  { id: 3, name: 'wo' },
  { id: 4, name: 'do' },
  { id: 5, name: 'vr' },
  { id: 6, name: 'za' },
  { id: 7, name: 'zo' },
]

const FREQUENCY_LABELS = {
  '7,day': 'Wekelijks',
  '14,day': 'Tweewekelijks',
  '1,month': 'Maandelijks',
  // oude frequenties die nog op bestaande events kunnen staan
  '6,month': 'Halfjaarlijks (oud)',
  '1,year': 'Jaarlijks (oud)',
}

export const isRecurring = (recurring) =>
  !!recurring && recurring !== NOT_RECURRING

// kalenderdag (Europe/Amsterdam) van een ISO-string, Date of DateTime
export const toDay = (value) => {
  if (!value) return null
  let dt
  if (DateTime.isDateTime(value)) dt = value.setZone(ZONE)
  else if (value instanceof Date)
    dt = DateTime.fromJSDate(value, { zone: ZONE })
  else dt = DateTime.fromISO(String(value), { zone: ZONE })
  return dt.isValid ? dt.startOf('day') : null
}

// laatste dag die een reeks vanaf `from` mag bereiken (ISO yyyy-mm-dd)
export const seriesLimit = (from) => {
  const day = toDay(from)
  return day ? day.plus({ months: MAX_SERIES_MONTHS }).toISODate() : null
}

export const formatDay = (value) => {
  const day = toDay(value)
  return day ? day.setLocale('nl').toFormat('d LLLL yyyy') : ''
}

export const weekdayOf = (value) => {
  const day = toDay(value)
  return day ? day.weekday : null
}

const normalizeWeekdays = (weekdays) =>
  [...new Set((weekdays || []).map(Number))]
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 7)
    .sort((a, b) => a - b)

export const weekdayLabels = (weekdays) =>
  normalizeWeekdays(weekdays)
    .map((n) => WEEKDAY_CHOICES[n - 1].name)
    .join(', ')

// weekdagen van een wekelijkse reeks; standaard de weekdag van de startdatum
export const seriesWeekdays = (record) => {
  if (!record || record.recurring !== WEEKLY) return []
  const weekdays = normalizeWeekdays(record.recurringWeekdays)
  if (weekdays.length) return weekdays
  const start = weekdayOf(record.startDate)
  return start ? [start] : []
}

// Nederlandse omschrijving, bv. 'Wekelijks (di, do) t/m 2 februari 2027'
export const seriesLabel = (record) => {
  if (!record || !isRecurring(record.recurring)) return 'Nee'
  const frequency = FREQUENCY_LABELS[record.recurring] || record.recurring
  const days = weekdayLabels(seriesWeekdays(record))
  const until = record.endDate ? ` t/m ${formatDay(record.endDate)}` : ''
  return `${frequency}${days ? ` (${days})` : ''}${until}`
}

// Validatie van het aanmaakformulier (SimpleForm `validate`); spiegelt de API
export const validateSeries = (values = {}) => {
  const errors = {}
  if (!isRecurring(values.recurring)) return errors

  if (!SUPPORTED_RECURRING.includes(values.recurring)) {
    errors.recurring = 'Kies wekelijks, tweewekelijks of maandelijks'
    return errors
  }
  const start = toDay(values.startDate)
  const end = toDay(values.endDate)
  if (!end) {
    errors.endDate = 'Verplicht bij een herhalende activiteit'
  } else if (start && end < start) {
    errors.endDate = 'De einddatum ligt vóór de startdatum'
  } else if (start && end > toDay(seriesLimit(start))) {
    errors.endDate = `Een reeks loopt maximaal 3 maanden: uiterlijk ${formatDay(
      seriesLimit(start)
    )}`
  }
  return errors
}

// Payload voor POST /event: alleen reeksvelden meesturen die bij de keuze
// horen; de weekdag van de startdatum zit er bij wekelijks altijd in.
export const transformSeries = (data = {}) => {
  const { endDate, recurringWeekdays, ...rest } = data
  if (!isRecurring(data.recurring)) {
    return { ...rest, recurring: NOT_RECURRING }
  }
  const result = { ...rest, endDate }
  if (data.recurring === WEEKLY) {
    const weekdays = normalizeWeekdays(recurringWeekdays)
    const startWeekday = weekdayOf(data.startDate)
    if (weekdays.length) {
      result.recurringWeekdays = normalizeWeekdays(
        startWeekday ? [...weekdays, startWeekday] : weekdays
      )
    }
  }
  return result
}

// '19:30' → '19:30:00'. De API parset startTime als HH:mm:ss; een tijd zonder
// seconden laat de startdatum-validatie crashen (luxon: Invalid unit value NaN).
export const normalizeTime = (value) => {
  if (typeof value !== 'string') return value
  const match = value.trim().match(/^(\d{1,2})[:.](\d{2})(?::(\d{2}))?$/)
  if (!match) return value
  const [, h, m, sec] = match
  return `${h.padStart(2, '0')}:${m}:${sec || '00'}`
}

// transform van EventCreate: tijden normaliseren en reeksvelden opschonen
export const transformEventCreate = (data = {}) =>
  transformSeries({
    ...data,
    startTime: normalizeTime(data.startTime),
    endTime: normalizeTime(data.endTime),
  })

const ERROR_MESSAGES = {
  RECURRING_UNSUPPORTED:
    'Deze herhaling wordt niet ondersteund: kies wekelijks, tweewekelijks of maandelijks.',
  RECURRING_START_DATE_REQUIRED:
    'Een herhalende activiteit heeft een startdatum nodig.',
  RECURRING_END_DATE_REQUIRED:
    'Een herhalende activiteit heeft een einddatum nodig.',
  RECURRING_END_BEFORE_START: 'De einddatum ligt vóór de startdatum.',
  RECURRING_WEEKDAYS_NOT_ALLOWED:
    'Weekdagen kun je alleen bij een wekelijkse reeks kiezen.',
  RECURRING_WEEKDAYS_INVALID: 'Kies geldige weekdagen (ma t/m zo).',
  RECURRING_START_NOT_IN_WEEKDAYS:
    'De weekdag van de startdatum moet bij de gekozen weekdagen zitten.',
  RECURRING_NOT_EXTENDABLE:
    'Deze reeks kan niet verlengd worden (eenmalig, oude herhaling, geannuleerd of zonder data).',
  RECURRING_END_NOT_AFTER_CURRENT:
    'De nieuwe einddatum moet na het huidige einde van de reeks liggen.',
  RECURRING_END_DATE_INVALID: 'De einddatum is geen geldige datum.',
}

// Leesbare melding voor een (422) API-fout; HttpError.body is de JSON-body
export const recurringErrorMessage = (error) => {
  const apiError = (error && error.body && error.body.error) || {}
  const code = apiError.code
  if (code === 'RECURRING_TOO_LONG') {
    const max = apiError.properties && apiError.properties.maxEndDate
    return max
      ? `Een reeks loopt maximaal 3 maanden: kies uiterlijk ${formatDay(max)}.`
      : 'Een reeks loopt maximaal 3 maanden.'
  }
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code]
  return (error && error.message) || 'Er ging iets fout...'
}

// Een reeks is te verlengen als hij actief is en een ondersteunde frequentie heeft
export const isExtendable = (record) =>
  !!record &&
  !record.cancelled &&
  SUPPORTED_RECURRING.includes(record.recurring)

// Standaard- en maximale nieuwe einddatum: laatste datum + 3 maanden
export const extendTarget = (lastDateTime) => seriesLimit(lastDateTime)

// POST /event/:id/extendSeries. Via de geauthenticeerde fetch-wrapper i.p.v.
// dataProvider.create, want die gooit de response-body weg (alleen { id }).
export const extendSeries = async ({
  eventId,
  endDate,
  httpClient = fetchJson,
}) => {
  const resp = await httpClient(
    `${config.apiUrl}/event/${eventId}/extendSeries`,
    {
      method: 'POST',
      body: JSON.stringify(endDate ? { endDate } : {}),
    }
  )
  return resp.json || {}
}

export const extendResultMessage = ({ endDate, createdDates } = {}) => {
  const count = Number(createdDates) || 0
  const dates = count === 1 ? '1 nieuwe datum' : `${count} nieuwe data`
  return `Reeks verlengd t/m ${formatDay(endDate)} (${dates})`
}
